import { getLocale } from "@/shared/i18n";

const localeTag = (): string => (getLocale() === "en" ? "en-GB" : getLocale());

/**
 * Prices arrive as integer cents plus an ISO currency (SPEC §5). Formatting is
 * centralised so `€40` never renders as `40.00 EUR` on one screen and
 * `€40.00` on another.
 */
export const formatPrice = (
  cents: number | null | undefined,
  currency = "eur"
): string | null => {
  if (cents === null || cents === undefined) return null;
  const value = cents / 100;
  const formatted = new Intl.NumberFormat(localeTag(), {
    style: "currency",
    currency: currency.toUpperCase(),
    // Whole-euro prices (25, 40, 300, 600) read better without ",00".
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
  return formatted;
};

/** "14 March 2026" — long-form, matching the editorial tone. */
export const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(localeTag(), {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

/** "14 MAR" — compact, for dense feeds and tables. */
export const formatDateShort = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(localeTag(), {
    day: "2-digit",
    month: "short",
  }).format(date);
};

export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(localeTag(), {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

/** `0x1a2b…9f0e` for wallet addresses. */
export const shortenAddress = (address: string | null | undefined): string => {
  if (!address) return "";
  return address.length <= 12
    ? address
    : `${address.slice(0, 6)}…${address.slice(-4)}`;
};

/** Two-letter monogram for the account control, e.g. "Anne Weber" → "AW". */
export const initialsOf = (
  name: string | null | undefined,
  email?: string | null
): string => {
  const source = (name || email || "").trim();
  if (!source) return "—";
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};
